import { useCallback, useEffect, useState } from "react";
import type { GeschaeftspartnerTyp } from "@/lib/kontakte";
import {
  clampGeschaeftspartnerColumnWidth,
  getDefaultGeschaeftspartnerColumns,
  normalizeGeschaeftspartnerColumnSelection,
  type GeschaeftspartnerColumnKey,
} from "@/pages/kontakte/geschaeftspartnerColumns";

const STORAGE_KEYS: Record<GeschaeftspartnerTyp, string> = {
  kunde: "eh_kontakte_kunden_table_columns_v1",
  lieferant: "eh_kontakte_lieferanten_table_columns_v1",
};

export interface GeschaeftspartnerColumnSettings {
  /** null = Standard-Spalten verwenden. */
  columns: GeschaeftspartnerColumnKey[] | null;
  widths: Record<string, number>;
}

const EMPTY_SETTINGS: GeschaeftspartnerColumnSettings = { columns: null, widths: {} };

function readStored(typ: GeschaeftspartnerTyp): GeschaeftspartnerColumnSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS[typ]);
    if (!raw) return { ...EMPTY_SETTINGS };
    const parsed = JSON.parse(raw) as GeschaeftspartnerColumnSettings;
    return {
      columns: parsed.columns?.length
        ? normalizeGeschaeftspartnerColumnSelection(parsed.columns, typ)
        : null,
      widths: parsed.widths && typeof parsed.widths === "object" ? parsed.widths : {},
    };
  } catch {
    return { ...EMPTY_SETTINGS };
  }
}

function writeStored(typ: GeschaeftspartnerTyp, settings: GeschaeftspartnerColumnSettings): void {
  try {
    localStorage.setItem(STORAGE_KEYS[typ], JSON.stringify(settings));
  } catch {
    // localStorage nicht verfügbar (z. B. Privatmodus) – Einstellungen gelten nur für die Sitzung.
  }
}

export function useGeschaeftspartnerTableSettings(typ: GeschaeftspartnerTyp) {
  const [settings, setSettings] = useState<GeschaeftspartnerColumnSettings>(() => readStored(typ));

  useEffect(() => {
    setSettings(readStored(typ));
  }, [typ]);

  const saveSettings = useCallback(
    (next: GeschaeftspartnerColumnSettings) => {
      setSettings(next);
      writeStored(typ, next);
    },
    [typ],
  );

  return { settings, saveSettings };
}

export function getVisibleGeschaeftspartnerColumns(
  settings: GeschaeftspartnerColumnSettings,
  typ: GeschaeftspartnerTyp,
): GeschaeftspartnerColumnKey[] {
  if (settings.columns?.length) {
    return normalizeGeschaeftspartnerColumnSelection(settings.columns, typ);
  }
  return getDefaultGeschaeftspartnerColumns(typ);
}

export function getVisibleGeschaeftspartnerWidths(
  settings: GeschaeftspartnerColumnSettings,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(settings.widths ?? {})) {
    const num = Number(value);
    if (Number.isFinite(num) && num > 0) out[key] = clampGeschaeftspartnerColumnWidth(num);
  }
  return out;
}
